import React from "react"
import TableWithPagination from "../table/TableWithPagination.jsx"
import OrderStatusBadge from "./OrderStatusBadge.jsx"
import {
    getOrderFilterLabelForColumn,
    isOrderColumnFilterable,
} from "../filters/orderSearchFilters.js"

function renderTextCell(value, className = "", style = {}, cellProps = {}) {
    const displayValue = value ?? ""
    return (
        <td
            style={{ ...(cellProps.style ?? {}), ...style }}
            className={`${className} ${cellProps.className ?? ""}`.trim()}
        >
            <span
                title={displayValue}
                style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {displayValue}
            </span>
        </td>
    )
}

function renderComponentCell(content, style = {}, className = "", cellProps = {}) {
    return (
        <td
            style={{
                whiteSpace: "nowrap",
                width: "1%",
                ...(cellProps.style ?? {}),
                ...style,
            }}
            className={`${className} ${cellProps.className ?? ""}`.trim()}
        >
            {content}
        </td>
    )
}

/**
 * @param {{
 *   orders: any[],
 *   isLoadingInitial?: boolean,
 *   totalRows?: number,
 *   page?: number,
 *   perPage?: number,
 *   onPageChange?: (nextPage: number) => void,
 *   onPerPageChange?: (nextPerPage: number | "fit") => void,
 *   perPageSelection?: number | "fit",
 *   onFitPerPageChange?: (rows: number) => void,
 *   onOrderClick?: (order: any) => void,
 *   sortBy?: string,
 *   sortDirection?: "asc" | "desc",
 *   onSort?: (columnKey: string, direction: "asc" | "desc" | null) => void,
 *   onAddFilter?: (columnKey: string) => void,
 *   activeFilterLabels?: Set<string>,
 *   showPagination?: boolean,
 *   showRefetchLoader?: boolean,
 *   enableSelection?: boolean,
 *   onSelectionChange?: (selectedOrders: any[]) => void,
 *   columns?: Array<{
 *     key: string,
 *     label: any,
 *     sortable?: boolean,
 *     filterable?: boolean,
 *     sortAscLabel?: string,
 *     sortDescLabel?: string,
 *     preferredWidth?: number,
 *     minWidth?: number,
 *     cellType?: "text" | "status" | "custom",
 *     value?: (order: any) => any,
 *     render?: (order: any) => any,
 *     headerClassName?: string,
 *     headerStyle?: any,
 *     cellClassName?: string,
 *     cellStyle?: any,
 *   }>,
 * }} props
 */
export default function OrdersDesktopTable({
    orders,
    isLoadingInitial = false,
    totalRows = 0,
    page = 1,
    perPage = 25,
    onPageChange,
    onPerPageChange,
    perPageSelection = undefined,
    onFitPerPageChange = undefined,
    onOrderClick,
    sortBy = "orderDate",
    sortDirection = "desc",
    onSort = () => { },
    onAddFilter = undefined,
    activeFilterLabels = undefined,
    showPagination = true,
    showRefetchLoader = false,
    enableSelection = true,
    onSelectionChange = undefined,
    columns = undefined,
}) {
    const getOrderKey = React.useCallback((order, index) => {
        return order?.id ?? order?.orderId ?? order?.master ?? index
    }, [])

    const [selectedOrderKeys, setSelectedOrderKeys] = React.useState(/** @returns {any[]} */ () => [])

    React.useEffect(() => {
        if (!enableSelection) {
            if (selectedOrderKeys.length) {
                setSelectedOrderKeys([])
            }
            return
        }
        // Keep selection scoped to the currently rendered page of rows.
        const availableKeys = new Set(orders.map(getOrderKey))
        setSelectedOrderKeys((prev) => prev.filter((key) => availableKeys.has(key)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orders, getOrderKey, enableSelection])

    const selectedOrderKeySet = React.useMemo(
        () => new Set(selectedOrderKeys),
        [selectedOrderKeys]
    )
    const selectedOrders = React.useMemo(
        () => orders.filter((order, index) => selectedOrderKeySet.has(getOrderKey(order, index))),
        [orders, selectedOrderKeySet, getOrderKey]
    )

    React.useEffect(() => {
        if (!enableSelection || typeof onSelectionChange !== "function") {
            return
        }
        onSelectionChange(selectedOrders)
    }, [selectedOrders, onSelectionChange, enableSelection])
    const allOrderKeys = React.useMemo(
        () => orders.map(getOrderKey),
        [orders, getOrderKey]
    )
    const hasOrders = allOrderKeys.length > 0
    const selectedCount = selectedOrderKeys.length
    const isAllSelected = hasOrders && selectedCount === allOrderKeys.length
    const isIndeterminate = hasOrders && selectedCount > 0 && !isAllSelected

    const headerCheckboxRef = React.useRef(/** @type {HTMLInputElement | null} */ (null))
    React.useEffect(() => {
        if (!enableSelection) {
            return
        }
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = isIndeterminate
        }
    }, [isIndeterminate, enableSelection])

    function toggleAllOrders(nextChecked) {
        setSelectedOrderKeys(nextChecked ? allOrderKeys : [])
    }

    function toggleOrder(key, nextChecked) {
        setSelectedOrderKeys((prev) => {
            const next = new Set(prev)
            if (nextChecked) {
                next.add(key)
            } else {
                next.delete(key)
            }
            return Array.from(next)
        })
    }

    const SELECT_CHECKBOX_STYLE = {
        width: "1rem",
        height: "1rem",
        marginTop: 0,
    }
    const HEADER_CLASS = "text-uppercase text-secondary"
    const HEADER_STYLE = { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.2 }
    const BASE_HEADER = { className: HEADER_CLASS, style: HEADER_STYLE }

    function makeHeader(col) {
        const filterable = (
            col.filterable === true ||
            (col.filterable !== false && isOrderColumnFilterable(col.key))
        )
        const filterLabel = getOrderFilterLabelForColumn(col.key)
        const isFilterActive = Boolean(
            filterLabel && activeFilterLabels?.has(filterLabel)
        )

        return {
            key: col.key,
            label: col.label,
            ...BASE_HEADER,
            sortable: Boolean(col.sortable),
            filterable,
            sortKey: col.key,
            sortBy,
            sortDirection,
            sortAscLabel: col.sortAscLabel,
            sortDescLabel: col.sortDescLabel,
            isFilterActive,
            ...(col.sortable ? {
                onSort: (columnKey, direction) => onSort(columnKey, direction),
            } : {}),
            ...(filterable && typeof onAddFilter === "function" ? {
                onFilter: () => onAddFilter(col.key),
            } : {}),
            preferredWidth: col.preferredWidth,
            minWidth: col.minWidth,
            className: col.headerClassName ?? HEADER_CLASS,
            style: { ...HEADER_STYLE, ...(col.headerStyle ?? {}) },
        }
    }

    const defaultColumns = [
        { key: "location", label: "Location", filterable: true, preferredWidth: 100, cellType: "text", value: (o) => o.location },
        { key: "master", label: "Master", sortable: true, filterable: true, preferredWidth: 105, cellType: "text", value: (o) => o.master, cellClassName: "fw-semibold", cellStyle: { color: "#0f6170" } },
        { key: "status", label: "Status", sortable: true, filterable: true, minWidth: 150, preferredWidth: 150, cellType: "status", value: (o) => o.status },
        { key: "firstName", label: "First Name", filterable: true, preferredWidth: 120, cellType: "text", value: (o) => o.firstName },
        { key: "lastName", label: "Last Name", filterable: true, preferredWidth: 140, cellType: "text", value: (o) => o.lastName },
        { key: "sjNum", label: "SJ Num", sortable: true, filterable: true, preferredWidth: 105, cellType: "text", value: (o) => o.sjNum },
        { key: "shipTo", label: "Ship To", filterable: true, preferredWidth: 140, cellType: "text", value: (o) => o.shipTo },
        {
            key: "orderDate",
            label: "Order Date",
            sortable: true,
            preferredWidth: 90,
            cellType: "text",
            value: (o) => o.orderDate,
            sortAscLabel: "Sort oldest first",
            sortDescLabel: "Sort newest first",
        },
    ]

    const effectiveColumns = Array.isArray(columns) && columns.length ? columns : defaultColumns

    const headers = [
        ...(enableSelection ? [{
            key: "select",
            label: (
                <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="form-check-input align-middle"
                    style={SELECT_CHECKBOX_STYLE}
                    checked={isAllSelected}
                    onChange={(event) => toggleAllOrders(event.target.checked)}
                    onClick={(event) => event.stopPropagation()}
                />
            ),
            headerMenu: false,
            minWidth: 35,
            preferredWidth: 35,
            className: HEADER_CLASS,
            style: HEADER_STYLE,
        }] : []),
        ...effectiveColumns.map((col) => makeHeader(col)),
    ]

    function renderOrderRow(order, index, tableTools) {
        const getCellProps = tableTools?.getCellProps ?? (() => ({}))
        const orderKey = getOrderKey(order, index)
        const isChecked = selectedOrderKeySet.has(orderKey)

        return (
            <tr
                key={index}
                className="cursor-pointer"
                style={{ cursor: "pointer" }}
                onClick={() => {
                    if (onOrderClick) {
                        onOrderClick(order)
                    }
                }}
            >
                {enableSelection ? renderComponentCell(
                    <input
                        type="checkbox"
                        className="form-check-input align-middle"
                        style={SELECT_CHECKBOX_STYLE}
                        checked={isChecked}
                        onChange={(event) => toggleOrder(orderKey, event.target.checked)}
                        onClick={(event) => event.stopPropagation()}
                    />,
                    {},
                    "",
                    getCellProps("select")
                ) : null}
                {effectiveColumns.map((col) => {
                    const cellProps = getCellProps(col.key)
                    const baseStyle = { fontSize: "0.875rem", ...(col.cellStyle ?? {}) }
                    const className = col.cellClassName ?? "text-dark"
                    if (col.cellType === "status") {
                        const status = col.value ? col.value(order) : order[col.key]
                        return renderComponentCell(
                            <OrderStatusBadge status={status} />,
                            {},
                            "",
                            cellProps
                        )
                    }
                    if (col.cellType === "custom" && typeof col.render === "function") {
                        return renderComponentCell(col.render(order), {}, "", cellProps)
                    }
                    const value = col.value ? col.value(order) : order[col.key]
                    return renderTextCell(value, className, baseStyle, cellProps)
                })}
            </tr>
        )
    }

    return (
        <TableWithPagination
            headers={headers}
            rows={orders}
            renderRow={renderOrderRow}
            isLoadingInitial={isLoadingInitial}
            totalRows={totalRows}
            page={page}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
            perPageSelection={perPageSelection}
            onFitPerPageChange={onFitPerPageChange}
            stretchFooterToBottom={true}
            showPagination={showPagination}
            showRefetchLoader={showRefetchLoader}
        />
    )
}
